import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import API from "./API";
import axios from "axios";

const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB

const loadImageFromBlob = (blob: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("이미지를 불러오지 못했습니다."));
    };

    img.src = objectUrl;
  });

const compressBlobImage = async (
  blob: Blob,
  options: {
    maxWidth: number;
    maxHeight: number;
    quality: number;
    outputType?: "image/jpeg" | "image/png";
  },
): Promise<Blob> => {
  const { maxWidth, maxHeight, quality, outputType = "image/jpeg" } = options;

  const img = await loadImageFromBlob(blob);

  let targetWidth = img.width;
  let targetHeight = img.height;

  if (targetWidth > maxWidth || targetHeight > maxHeight) {
    const ratio = Math.min(maxWidth / targetWidth, maxHeight / targetHeight);
    targetWidth = Math.round(targetWidth * ratio);
    targetHeight = Math.round(targetHeight * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas context를 생성할 수 없습니다.");
  }

  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const compressedBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (!result) {
          reject(new Error("이미지 압축에 실패했습니다."));
          return;
        }
        resolve(result);
      },
      outputType,
      quality,
    );
  });

  return compressedBlob;
};

const compressUntilUnderLimit = async (
  originalBlob: Blob,
  maxSizeBytes: number,
): Promise<Blob> => {
  // 점진적으로 해상도/품질을 낮추면서 20MB 이하로 맞춤
  const resizeSteps = [
    { maxWidth: 2600, maxHeight: 2600 },
    { maxWidth: 2200, maxHeight: 2200 },
    { maxWidth: 1920, maxHeight: 1920 },
    { maxWidth: 1600, maxHeight: 1600 },
    { maxWidth: 1280, maxHeight: 1280 },
  ];

  const qualitySteps = [0.9, 0.82, 0.75, 0.68, 0.6, 0.5];

  let lastBlob: Blob = originalBlob;

  for (const resize of resizeSteps) {
    for (const quality of qualitySteps) {
      const compressed = await compressBlobImage(originalBlob, {
        ...resize,
        quality,
        outputType: "image/jpeg",
      });

      lastBlob = compressed;

      if (compressed.size <= maxSizeBytes) {
        return compressed;
      }
    }
  }

  return lastBlob;
};

const makeHtmltoImage = {
  _makeImageHandler: async (reNum: string) => {
    // const imageFiles = await makeHtmltoImage._convertToImg();
    // await makeHtmltoImage._sendImgToServer(imageFiles, reNum);
  },

  _convertToImg: async (className: string) => {
    // html to imageFile
    const page: any = document.querySelector(className);
    const canvas = await html2canvas(page, {
      scale: 2,
      logging: true,
      width: page.offsetWidth,
      height: page.offsetHeight,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
    });

    const imageFile = canvas.toDataURL("image/png", 1.0);

    return imageFile;
  },

  _sendImgToServer: async (imageFileDataUrl: string[], reNum: string) => {
    const blob1 = await fetch(imageFileDataUrl[0]).then((res) => res.blob());
    const blob2 = await fetch(imageFileDataUrl[1]).then((res) => res.blob());

    const formData = new FormData();
    formData.append("imageData", blob1, "Page1.png");
    formData.append("type", "png");
    formData.append("imageData", blob2, "Page2.png");
    formData.append("type", "png");

    const accessToken = localStorage.getItem("accessToken");
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    };

    const response = await axios.post(
      `https://homenmove.net/v1/api/receipt/contract-image/${reNum}`,
      formData,
      config,
    );

    if (response) {
      alert("성공적으로 계약서 저장");
    } else {
      alert("계약서 저장 실패!");
    }
  },

  _sendImgToServer2: async (imageFileDataUrl: string[], reNum: string) => {
    try {
      if (!imageFileDataUrl || imageFileDataUrl.length === 0) {
        throw new Error("업로드할 이미지가 없습니다.");
      }

      const accessToken = localStorage.getItem("accessToken");
      const formData = new FormData();

      for (let i = 0; i < imageFileDataUrl.length; i++) {
        const originalBlob = await fetch(imageFileDataUrl[i]).then((res) =>
          res.blob(),
        );

        console.log(
          `원본 ${i + 1}: ${(originalBlob.size / 1024 / 1024).toFixed(2)}MB`,
        );

        const compressedBlob = await compressUntilUnderLimit(
          originalBlob,
          MAX_IMAGE_SIZE,
        );

        console.log(
          `압축 ${i + 1}: ${(compressedBlob.size / 1024 / 1024).toFixed(2)}MB`,
        );

        if (compressedBlob.size > MAX_IMAGE_SIZE) {
          throw new Error(
            `${i + 1}번째 이미지가 20MB 이하로 압축되지 않았습니다.`,
          );
        }

        formData.append("imageData", compressedBlob, `Page${i + 1}.jpg`);
        formData.append("type", "jpg");
      }

      const response = await axios.post(
        `https://homenmove.net/v1/api/receipt/upload-image/${reNum}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response) {
        alert("성공적으로 CS 계약서가 업로드되었습니다.");
      } else {
        alert("통인CS에 계약서 저장 실패");
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
};

export default makeHtmltoImage;
